import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./surchargerule.css";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");
const API_RULES = `${API_BASE}/superuser/surcharges/`;

function money(n) {
  const x = Number(n || 0);
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(x);
}

export default function SurchargeRules() {
  const access = useStore((s) => s.user.access);

  const apiFetch = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
          ...(options.headers || {}),
        },
      });
      return res;
    },
    [access]
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("total");
  const [kind, setKind] = useState("flat");
  const [value, setValue] = useState("10");
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const res = await apiFetch(API_RULES, { method: "GET" });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${text}`.trim());
        }

        const data = await res.json();
        if (alive) setItems(Array.isArray(data) ? data : data?.results || []);
      } catch (e) {
        if (alive) setErr(e?.message || "Erreur.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [apiFetch]);

  const preview = useMemo(() => {
    const v = Number(value || 0);
    if (kind === "percent") return `${v}%`;
    return money(v);
  }, [kind, value]);

  async function onAdd() {
    setErr("");

    const payload = {
      name: name.trim(),
      target,
      kind,
      value: Number(value || 0),
      enabled: Boolean(enabled),
    };

    if (!payload.name) {
      setErr("Nom obligatoire.");
      return;
    }

    try {
      const res = await apiFetch(API_RULES, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${text}`.trim());
      }

      const created = await res.json();
      setItems((prev) => [created, ...(prev || [])]);

      setName("");
      setTarget("total");
      setKind("flat");
      setValue("10");
      setEnabled(true);
    } catch (e) {
      setErr(e?.message || "Erreur.");
    }
  }

  async function onDelete(id) {
    setErr("");
    try {
      const res = await apiFetch(`${API_RULES}${id}/`, { method: "DELETE" });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${text}`.trim());
      }

      setItems((prev) => (prev || []).filter((x) => x.id !== id));
    } catch (e) {
      setItems((prev) => (prev || []).filter((x) => x.id !== id));
      setErr(e?.message || "Erreur.");
    }
  }

  function toggle(id) {
    setItems((prev) => (prev || []).map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));
  }

  return (
    <div className="boSR">
      <div className="boSRHeader">
        <div>
          <h1 className="boSRTitle">Surcharge Rules</h1>
          {/* <div className="boSRSub">Règles simples V1 (flat ou % sur un poste)</div> */}
        </div>
      </div>

      {err ? (
        <div className="boCard boError">
          <div className="boErrorTitle">Erreur</div>
          <div className="boErrorText">{err}</div>
        </div>
      ) : null}

      <div className="boSRGrid">
        <div className="boCard">
          <div className="boSectionTitle">Créer une règle</div>

          <div className="boForm">
            <label className="boLabel">
              Nom de la règle
              <input className="boInput" value={name} onChange={(e) => setName(e.target.value)} />
            </label>

            <div className="boRow2">
              <label className="boLabel">
                Cible
                <select className="boSelect" value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="pcb">pcb</option>
                  <option value="assembly">assembly</option>
                  <option value="components">components</option>
                  <option value="shipping">shipping</option>
                  <option value="total">total</option>
                </select>
              </label>

              <label className="boLabel">
                Type
                <select className="boSelect" value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="flat">flat</option>
                  <option value="percent">percent</option>
                </select>
              </label>
            </div>

            <label className="boLabel">
              Valeur ({kind === "percent" ? "%" : "€"}) preview: <span className="mono">{preview}</span>
              <input
                className="boInput mono"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                inputMode="decimal"
              />
            </label>

            <label className="boLabel boRow">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Active
            </label>

            <button className="boBtn boBtnPrimary" onClick={onAdd}>
              Ajouter
            </button>
          </div>
        </div>

        <div className="boCard">
          <div className="boSectionTitle">Règles</div>

          {loading ? (
            <>
              <div className="boSkeletonRow" />
              <div className="boSkeletonRow" />
              <div className="boSkeletonRow" />
            </>
          ) : items.length === 0 ? (
            <div className="boEmptyText">Aucune règle.</div>
          ) : (
            <div className="boRules">
              {items.map((r) => (
                <div className="boRule" key={r.id}>
                  <div>
                    <div className="boRuleTitle">{r.name}</div>
                    <div className="boRuleSub mono">
                      target={r.target} - {r.kind}={r.kind === "percent" ? `${r.value}%` : `${r.value}€`}
                    </div>
                  </div>

                  <div className="boRuleActions">
                    <button
                      className={`boPill ${r.enabled ? "boPillOn" : "boPillOff"}`}
                      onClick={() => toggle(r.id)}
                    >
                      {r.enabled ? "On" : "Off"}
                    </button>
                    <button className="boBtn boBtnGhost" onClick={() => onDelete(r.id)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
